import downloadSvg from "assets/img/download.svg";
import loadingSvg from "assets/img/loading.svg";
import Button from "components/Button";
import ButtonWithConfirmation, { DialogIcon } from "components/ButtonWithConfirmation";
import { Card } from "components/Card";
import Dropdown from "components/Dropdown";
import Form from "components/Form";
import Icon from "components/Icon";
import { LightThemeTableWithPagination } from "components/LightThemeTableWithPagination";
import Link from "components/Link";
import { Tooltip } from "components/Tooltip";
import { RouteProps } from "containers/App";
// import { FilterOptions } from "containers/ProjectFilter";
import Constants from "helpers/constants";
import { getItemJson, removeItem, setItem } from "helpers/storage";
import { DirFile, DirFilesQueryParams, DirFileVersion } from "interfaces/dir-file";
import { PaginatedQuery } from "interfaces/paginated-query";
import { ReactSelectObject } from "interfaces/select";
import { isNumber, memoize } from "lodash";
import moment from "moment";
import React from "react";
import "react-quill/dist/quill.snow.css";
import { connect, ConnectedProps } from "react-redux";
import { State } from "state";
import {
  createAnalyticsEvent,
  DIRDownloadSearchFields,
  DIRFileStatus,
  EventTypes,
} from "state/analytics";
import { fetchZipForIrisDownload, IRIS_FILE_LIMIT } from "state/batch_download";
import { clearDIRFiles, fetchDIRFiles, fetchDIRFilesCount, initDIRFiles } from "state/dir-files";
import WholesalerFilter, { FilterOptions } from "./WholesalerFilter";
import { Grid } from "@material-ui/core";
import config from "config";

type DirFileTableFields = DirFileVersion &
  Omit<DirFile<2>, "versions"> & {
    version: number;
    date: string;
    subRows: [];
  };

interface DirDownloadsState {
  currentWholesalerName: string;
  currentWholesalerNumber: string;
  deselectedIds: string[];
  disableRepeatDownload?: boolean;
  downloadMessage?: string;
  fields: Set<string>;
  filesPageIndex?: number;
  filesPageSize: number;
  filesSort?: string;
  isLoadingFiles: boolean;
  isSearchInProgress?: boolean;
  isSelectionInclusive: boolean;
  selectAllActive: boolean;
  selectedSurveyReasons: ReactSelectObject[];
  query: typeof defaultQuery;
  searchError: boolean;
  selectedIds: string[];
  selectionTotalCount: number;
  resetTable: boolean;
}

type DownloadsProps = PropsFromRedux & RouteProps;

export const IRIS_DOWNLOADS_PAGE_SIZE = 25;
export const IRIS_DOWNLOADS_DEFAULT_SORT = "created_at.desc";

const surveyReasonOptions = [
  { label: "Monthly Capture", value: "Monthly Capture" },
  { label: "Planogram Update Needed", value: "Planogram Update Needed" },
  { label: "Reset Request", value: "Reset Request" },
  { label: "Reset Completed", value: "Reset Completed" },
  { label: "No Cooler Door Capture", value: "No Cooler Door Capture" },
];

const defaultPaginationParams: PaginatedQuery = {
  sort: IRIS_DOWNLOADS_DEFAULT_SORT,
  size: IRIS_DOWNLOADS_PAGE_SIZE,
  page: 0,
};

const defaultQuery: DirFilesQueryParams & PaginatedQuery = {
  address: "",
  city: "",
  page: 0,
  size: IRIS_DOWNLOADS_PAGE_SIZE,
  sort: IRIS_DOWNLOADS_DEFAULT_SORT,
  state: "",
  storeName: "",
  wacn: "",
  wholesalerNumber: "",
  selectedSurveyReasonsValues: [],
  selectedSurveyReasons: [],
  zip: "",
  createdDate: "",
  ...defaultPaginationParams,
};

const defaultSelectionState = {
  isSelectionInclusive: false,
  deselectedIds: [],
  selectedIds: [],
  selectionTotalCount: 0,
  selectAllActive: false,
  resetTable: false,
};

const defaultState: DirDownloadsState = {
  currentWholesalerName: "",
  currentWholesalerNumber: "",
  fields: new Set(),
  filesPageIndex: 0,
  filesPageSize: IRIS_DOWNLOADS_PAGE_SIZE,
  filesSort: IRIS_DOWNLOADS_DEFAULT_SORT,
  isLoadingFiles: true,
  isSearchInProgress: false,
  query: defaultQuery,
  selectedSurveyReasons: [],
  searchError: false,
  ...defaultSelectionState,
};

const getTableDataFromFiles = memoize((files: any): DirFileTableFields[] => {
  const fileVersions = files.map((dirFile: any) => {
    const { store_name, store_address, wacn, versions, survey_reason } = dirFile;
    const commonProps = {
      store_name,
      store_address,
      wacn,
      survey_reason,
    };
    const previousVersions = versions.slice(1).map((v: any, i: any) => {
      commonProps.survey_reason = v.survey_reason;
      return {
        ...commonProps,
        ...v,
        date: v.created_at,
        version: (i + 1) * -1,
        subRows: false,
      };
    });

    commonProps.survey_reason = versions[0].survey_reason;
    const tableRow = {
      ...commonProps,
      ...versions[0],
      date: versions[0].created_at,
      version: 0,
      subRows: previousVersions.length > 0 ? previousVersions : [],
    };
    return tableRow as DirFileTableFields;
  });
  return fileVersions;
});

const CUSTOM_TOOLBAR_OPTIONS = [
  [{ header: "1" }, { header: "2" }, { font: [] }],
  [{ size: [] }],
  ["bold", "italic", "underline", "strike", "blockquote"],
  [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
  ["link"],
  ["clean"],
];

const modules = { toolbar: CUSTOM_TOOLBAR_OPTIONS };
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "clean",
];

// ==================== Helper Functions ====================

// ==================== Sub Components ====================
export const FormHeader: React.FC<{ headerText: string; isSubmitDisabled: boolean }> = ({
  headerText,
  isSubmitDisabled,
}) => (
  <div style={styles.header}>
    <h1 style={styles.title}>{headerText}</h1>
  </div>
);

export const InputField: React.FC<{
  label?: string;
  name?: string;
  value?: any;
  placeholder?: string;
  dataQa?: string;
  onChange: (value: any) => void;
  error?: string;
}> = ({ label, name, value, placeholder = "Announcement title", dataQa, onChange, error }) => (
  <div style={styles.inputGroup}>
    <div style={label ? styles.inputLabel : styles.blankLabel}>{label}</div>
    <Form.Input
      type="text"
      name={name}
      placeholder={placeholder}
      data-qa={dataQa}
      value={value}
      onChange={(event) => onChange(event)}
      style={{ ...styles.textInput, borderColor: error ? "#d32f2f" : "#ddd" }}
    />
    {error && <div style={styles.errorText}>{error}</div>}
  </div>
);

const WholesalerFilterV2: React.FC<{
  value?: string;
  onChange: (option: FilterOptions) => any;
}> = ({ value, onChange }) => (
  <div style={styles.inputGroup}>
    <div style={styles.inputLabel}>Wholesaler</div>
    <div
      data-qa="select-project"
      style={{ height: 44, border: "0px solid #ddd", borderRadius: 6, boxSizing: "border-box" }}
    >
      <WholesalerFilter
        name="select-project"
        value={value}
        minHeight={44}
        noShadow
        onChange={onChange} 
      />
    </div>
  </div>
);

export const InputDateField: React.FC<{
  label?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  dataQa?: string;
  onChange: (value: any) => void;
  error?: string;
}> = ({ label, value, name, placeholder = "Announcement title", dataQa, onChange, error }) => (
  <div style={styles.inputGroup}>
    <div style={styles.inputLabel}>{label}</div>
    <Form.Input
      type="date"
      name={name}
      placeholder={placeholder}
      data-qa={dataQa}
      value={value}
      onChange={(event) => onChange(event)}
      style={{ ...styles.textInput, borderColor: error ? "#d32f2f" : "#ddd" }}
    />
    {error && <div style={styles.errorText}>{error}</div>}
  </div>
);

// ==================== Main Component ====================
class DirDownloads extends React.Component<DownloadsProps, DirDownloadsState> {
  private initialFiles = [];

  public state = defaultState;

  public componentDidMount() {
    this.props.initDIRFiles();

    const query = getItemJson(Constants.IRIS_DOWNLOADS_QUERY);
    if (query) {
      this.setState({
        query: {
          ...query,
          ...defaultPaginationParams,
        },
      });
      this.setDIRFiles(query);
    } else {
      this.setDIRFiles(defaultQuery);
    }
  }

  componentDidUpdate(_prevProps: DownloadsProps, prevState: DirDownloadsState) {
    const { page: prevPage, size: prevSize, sort: prevSort } = prevState.query;
    const { page: currPage, size: currSize, sort: currSort } = this.state.query;
    const shouldFetchNewPage =
      prevPage !== currPage || prevSize !== currSize || prevSort !== currSort;
    if (shouldFetchNewPage) {
      this.props.fetchDIRFiles(this.state.query);
    }

    if (this.state.resetTable) {
      this.setState({
        ...defaultSelectionState,
      });
    }
  }

  public emailCount() {
    const fileCount = this.state.selectionTotalCount || 0;
    return Math.ceil(fileCount / IRIS_FILE_LIMIT);
  }

  public setDIRFiles(query: DirFilesQueryParams) {
    const { currentWholesalerNumber, currentWholesalerName, fields } = this.state;

    query = {
      ...query,
      ...defaultPaginationParams,
    };

    this.setState({
      isSearchInProgress: true,
      isLoadingFiles: true,
      ...defaultSelectionState,
    });

    this.props.fetchDIRFiles(query).then(
      () => {
        const { files = [] } = this.props;
        this.setState({
          isSearchInProgress: false,
          disableRepeatDownload: false,
          downloadMessage: "",
          isLoadingFiles: false,
        });

        this.props.createAnalyticsEvent({
          event: EventTypes.DIR_DOWNLOADS_SEARCH,
          eventType: "Action",
          wholesalerName: currentWholesalerName,
          wholesalerNumber: currentWholesalerNumber,
          searchResults: files.length,
          searchFieldsList: [...Array.from(fields)] as DIRDownloadSearchFields[],
          searchAction: "Search",
        });
      },
      (reason) => {
        this.setState({
          searchError: true,
          isLoadingFiles: false,
        });
      }
    );
  }

  public handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
    e.preventDefault();
    setItem(Constants.IRIS_DOWNLOADS_QUERY, this.state.query);
    this.setDIRFiles(this.state.query);
    this.setState({ resetTable: true });
  };

  public handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    // reset form and inputs
    this.setState({
      query: defaultQuery,
      fields: new Set(),
      resetTable: true,
    });
    this.setState({ selectedSurveyReasons: [] });
    removeItem(Constants.IRIS_DOWNLOADS_QUERY);
    this.props.clearDIRFiles();
    this.props.createAnalyticsEvent({
      event: EventTypes.DIR_DOWNLOADS_SEARCH,
      eventType: "Action",
      searchAction: "Clear",
    });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      query: {
        ...this.state.query,
        [e.currentTarget.name]: e.currentTarget?.value,
      },
      fields: this.state.fields.add(e.currentTarget.placeholder),
    });
  };

  public goSpotCheckClick = (e: React.MouseEvent<HTMLButtonElement>, surveyUrl: string) => {
    const { currentWholesalerName, currentWholesalerNumber } = this.state;
    this.props.createAnalyticsEvent({
      event: EventTypes.DIR_DOWNLOAD_FILES,
      eventType: "Action",
      wholesalerName: currentWholesalerName,
      wholesalerNumber: currentWholesalerNumber,
      downloadFileCount: 1,
      goSpotCheckSurveyLink: surveyUrl,
    });
    this.props.createAnalyticsEvent({
      event: EventTypes.DIR_DOWNLOAD_IRIS_SURVEY,
      eventType: "Action",
      wholesalerName: currentWholesalerName,
      wholesalerNumber: currentWholesalerNumber,
      downloadFileCount: 1,
      goSpotCheckSurveyLink: surveyUrl,
    });

    return window.open(surveyUrl);
  };

  public handleBatchDownload = (e: React.MouseEvent<HTMLFormElement>) => {
    const {
      selectedIds,
      deselectedIds,
      currentWholesalerName,
      currentWholesalerNumber,
      selectionTotalCount,
      selectAllActive,
    } = this.state;

    const deselectedFileIds = deselectedIds.map((id) => ({ id: id }));
    const selectedFileIds = selectedIds.map((id) => ({ id: id }));

    this.props.createAnalyticsEvent({
      event: EventTypes.DIR_DOWNLOAD_FILES,
      eventType: "Action",
      wholesalerName: currentWholesalerName,
      wholesalerNumber: currentWholesalerNumber,
      downloadFileCount: selectionTotalCount,
    });

    this.setState({
      ...this.state,
      disableRepeatDownload: true,
    });

    this.props
      .fetchZipForIrisDownload(
        this.state.query,
        selectedFileIds,
        deselectedFileIds,
        selectAllActive
      )
      .then(() => {
        if (this.props.hasError) {
          return;
        }
        this.setState({
          ...this.state,
          downloadMessage: Constants.DOWNLOAD_FILES_SUCCESS_MESSAGE,
          disableRepeatDownload: false,
        });
      })
      .catch(() =>
        this.setState({
          ...this.state,
          downloadMessage: Constants.DOWNLOAD_FILES_ERROR_MESSAGE,
          disableRepeatDownload: false,
        })
      );
  };

  public table: any;

  public handleDownloadClick = (version: number, url: string) => {
    let filePathArray;
    let fileName;
    const { currentWholesalerName, currentWholesalerNumber } = this.state;
    filePathArray = url.split("/");

    if (filePathArray[filePathArray.length - 1].indexOf(".") !== -1) {
      fileName = filePathArray[filePathArray.length - 1].replace(/%20/g, " ");
    }
    if (fileName) {
      this.props.createAnalyticsEvent({
        event: EventTypes.DIR_DOWNLOAD_FILES,
        eventType: "Action",
        wholesalerName: currentWholesalerName,
        wholesalerNumber: currentWholesalerNumber,
        downloadFileCount: 1,
        downloadFileStatus: version < 0 ? DIRFileStatus.PREVIOUS : DIRFileStatus.CURRENT,
        downloadFileName: fileName,
      });

      this.props.createAnalyticsEvent({
        event: EventTypes.DIR_DOWNLOAD_IRIS_PSA,
        eventType: "Action",
        wholesalerName: currentWholesalerName,
        wholesalerNumber: currentWholesalerNumber,
        downloadFileCount: 1,
        downloadFileStatus: version < 0 ? DIRFileStatus.PREVIOUS : DIRFileStatus.CURRENT,
        downloadFileName: fileName,
      });
    }
    return window.open(url);
  };

  public renderDownloadStatus() {
    if (this.props.isLoadingDownloads) {
      return <img className="spin-right" src={loadingSvg} alt="Loading Icon" />;
    } else if (this.props.hasError) {
      return (
        <span className="download-error error">
          {Constants.DOWNLOAD_FILES_DOWNLOAD_FAILED_MESSAGE}
        </span>
      );
    } else if (this.state.downloadMessage) {
      return <span className="download-message">{this.state.downloadMessage}</span>;
    }
    return;
  }

  selectedRows: string[] = [];
  isInclusive: boolean = true;

  public render() {
    const { isLoadingFiles, filesPageCount, filesPageIndex } = this.props;

    const {
      disableRepeatDownload,
      query: { wholesalerNumber, sort: filesSort },
      isSearchInProgress,
      selectedIds,
      selectAllActive,
      resetTable,
    } = this.state;

    const tableColumnsConfig = [
      {
        field: "checkbox",
        header: (
          <div>
            <label className="custom-checkbox-wrapper">
              <input
                type="checkbox"
                checked={this.state.selectAllActive}
                onChange={(e) => {
                  handleRowSelect(e, null, true);
                }}
                className="custom-checkbox"
              />
              <span className="custom-checkmark"></span>
            </label>
          </div>
        ),
        formatter: (_: any, row: any) => (
          <label className="custom-checkbox-wrapper">
            <input
              type="checkbox"
              checked={this.selectedRows.includes(row.id)}
              onChange={(e) => {
                handleRowSelect(e, row, false);
              }}
              className="custom-checkbox"
            />
            <span className="custom-checkmark"></span>
          </label>
        ),
      },
      {
        field: "store_name",
        header: "Store Name",
      },
      {
        field: "store_address",
        header: "Address",
      },
      {
        field: "wacn",
        header: "WACN",
        accessor: "wacn",
        dataQa: "wacn",
        width: 110,
      },
      {
        field: "survey_reason",
        header: "Survey Reason",
      },
      {
        field: "version",
        header: "Version",
        accessor: "version",
        dataQa: "version",
        disableSortBy: true,
        formatter: (_: any, row: any) => {
          const originalRow = row?.original;
          const value = row.version;
          return (
            <div aria-label={`${originalRow?.store_name} PSA File Version`}>
              {value < 0 ? "Previous" : "Current"}
            </div>
          );
        },
        width: 110,
      },
      {
        field: "created_at",
        header: "Created Date",
        formatter: (_: any, row: any) => {
          const value = row.created_at;
          return formatCreatedDate(value);
        },
      },
      {
        field: "blob_url",
        header: "PSA File",
        formatter: (_: any, row: any) => {
          return (
            <button
              title={`Download PSA for ${row.store_name}`}
              className="download-button"
              data-qa="download-file-button"
              // onClick={(e) => {
              //   this!.handleDownloadClick(row.version, row.blob_url);
              // }}
              onClick={(e) => {
                const apiUrl = `${config.API_SERVICE_URL}/api/dir/${encodeURIComponent(row.filename)}`;
                this!.handleDownloadClick(row.version, apiUrl);
              }}
            >
              <div>
                <Icon src={downloadSvg} />
              </div>
            </button>
          );
        },
      },
      {
        field: "survey_url",
        header: "Survey Results and Images",
        formatter: (_: any, row: any) => {
          return row.survey_url ? (
            <Link
              className="go-spot-check-survey"
              data-qa="go-spot-check-survey-link"
              title={`Go Spot Check Survey results for ${row.store_name}`}
              to={row.survey_url}
              style={{ color: "#E5B611" }}
            >
              Survey Results
            </Link>
          ) : (
            <div className="current-file-data">
              <span>No Survey Results Available.</span>
            </div>
          );
        },
      },
    ];

    let files: DirFileTableFields[] =
      this.props.files && this.props.files.length > 0
        ? getTableDataFromFiles(this.props.files)
        : this.initialFiles;

    let tableData: any = [];

    files.forEach((x) => {
      tableData.push(x)
      if (x.subRows && x.subRows.length > 0) {
        tableData.push(...x.subRows);
      }
    });

    const handleRowSelect = (e: React.ChangeEvent<HTMLInputElement>, row: any, action: boolean) => {
      if (!action) {
        if (e.target.checked) {
          this.selectedRows.push(row.id);
          if (this.selectedRows.length == 1 && !this.state.selectAllActive) {
            this.setState({ selectionTotalCount: 1 });
          } else {
            this.setState({ selectionTotalCount: this.state.selectionTotalCount + 1 });
          }

          // Select all, deselect each row, then reselect each row
          if (this.selectedRows.length === 0) {
            this.setState({ selectAllActive: true });
          }
        } else {
          this.selectedRows = this.selectedRows.filter((id) => id !== row.id);
          this.setState({
            selectionTotalCount: this.state.selectionTotalCount - 1,
            selectAllActive: false,
          });
        }
      } else {
        if (e.target.checked) {
          fetchDIRFilesCount(this.state.query)
            .then((response) => {
              const count = parseInt(response.data.fileCount, 10);
              if (isNumber(count)) {
                this.setState({
                  selectionTotalCount: count,
                });
              }
            })
            .finally(() => {
              this.setState({ selectAllActive: true });
            });

          tableData.map((x: any) => {
            if (!this.selectedRows.includes(x.id)) {
              this.selectedRows.push(x.id);
            }
          });
        } else {
          this.selectedRows = [];
          this.setState({ selectAllActive: false, selectionTotalCount: 0 });
          this.isInclusive = false;
        }
      }

      this.setState({
        isSelectionInclusive: this.isInclusive,
        selectedIds: this.selectedRows,
        //deselectedIds: deselectedRowIds,
      });
    };

    const shouldResetTable =
      (isSearchInProgress && filesPageIndex === 0 && files.length > 0) || resetTable;

    const handlePageChange = (page: number, pageSize: number) => {
      this.setState((prev) => ({
        query: {
          ...prev.query,
          page: Math.max(0, page - 1),
          size: pageSize,
        },
      }));
    };

    const handleSortChange = (field: string, order: "asc" | "desc") => {
      this.setState((prev) => ({
        query: {
          ...prev.query,
          page: 0,
          sort: `${field}.${order}`,
        },
      }));
    };

    return (
      <div
        style={{
          gap: 0,
          borderRadius: "16px",
          padding: "5px",
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <Card style={styles.container}>
          {/* <div style={styles.noticeBannerWrapper}>
            <div style={styles.noticeBanner}>
              ATTN: Support is aware of an issue involving missing Realograms and Surveys since August 12th. The team is actively working to retroactively generate any missing surveys.
            </div>
          </div> */}
          <h2 style={{ marginLeft: "15px", marginTop: "5px" }}>IRIS Downloads</h2>
          <Form onSubmit={this.handleSubmit} style={{ width: "100%", padding: "15px" }}>
            {/* <FormHeader headerText="IRIS Downloads" isSubmitDisabled={false} /> */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <WholesalerFilterV2
                  value={wholesalerNumber}
                  onChange={({ number, name }) => {
                    this.setState({
                      currentWholesalerNumber: number?.toString(),
                      currentWholesalerName: name,
                      query: {
                        ...this.state.query,
                        wholesalerNumber: number?.toString(),
                      },
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div style={styles.inputGroup}>
                  <div style={styles.inputLabel}>Survey</div>
                  <div
                    data-qa="select-project"
                    style={{
                      height: 44,
                      border: "0px solid #ddd",
                      borderRadius: 6,
                      boxSizing: "border-box",
                    }}
                  >
                    <Dropdown
                      containerStyles={{
                        marginLeft: "unset",
                        background: "white",
                        boxShadow: "none",
                      }}
                      controlStyles={{
                        minHeight: 44,
                        borderRadius: "16px",
                        // border: "none"
                      }}
                      placeholder="Select Survey Types"
                      options={surveyReasonOptions}
                      onChange={(selectedSurveyReasons: ReactSelectObject[]) => {
                        this.setState({ selectedSurveyReasons });
                        const selectedSurveyReasonsValues = selectedSurveyReasons.map(
                          (obj) => obj.value
                        );
                        this.setState({
                          query: {
                            ...this.state.query,
                            selectedSurveyReasonsValues: selectedSurveyReasonsValues,
                            selectedSurveyReasons: selectedSurveyReasons,
                          },
                        });
                      }}
                      isDisabled={false}
                      value={this.state.selectedSurveyReasons}
                      isMulti={true}
                      closeMenuOnSelect={false}
                      blurInputOnSelect={false}
                      isSearchable={true}
                    />
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InputField
                  name="storeName"
                  label="Store Details"
                  placeholder="Store Name"
                  data-qa="store-name-field"
                  value={this.state.query.storeName}
                  onChange={this.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InputField
                  name="wacn"
                  label="WACN"
                  placeholder="WACN"
                  data-qa="wacn-field"
                  value={this.state.query.wacn}
                  onChange={this.handleChange}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <InputField
                  name="address"
                  label="Address"
                  placeholder="Address"
                  data-qa="address-field"
                  value={this.state.query.address}
                  onChange={this.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InputField
                  name="city"
                  label="City"
                  placeholder="City"
                  data-qa="city-field"
                  value={this.state.query.city}
                  onChange={this.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InputField
                  name="state"
                  label="State"
                  placeholder="State"
                  data-qa="state-field"
                  value={this.state.query.state}
                  onChange={this.handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <InputField
                  name="zip"
                  label="Zip Code"
                  placeholder="Zip Code"
                  data-qa="zip-code-field"
                  value={this.state.query.zip}
                  onChange={this.handleChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <InputDateField
                  name="createdDate"
                  label="Survey Created Date"
                  placeholder="Survey Created Date"
                  data-qa="created-date-field"
                  value={this.state.query.createdDate}
                  onChange={this.handleChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    data-qa="submit-announcement"
                    disabled={false}
                    onClick={this.handleClear}
                    type="submit"
                    style={{
                      ...styles.sendButton,
                      width: "50%",
                      opacity: false ? 0.6 : 1,
                      cursor: false ? "not-allowed" : "pointer",
                      backgroundColor: "white",
                      color: "#E5B611",
                      border: "solid",
                      borderColor: "#E5B611",
                    }}
                  >
                    Clear
                  </button>

                  <button
                    data-qa="submit-announcement"
                    disabled={false}
                    onClick={() => this.handleSubmit}
                    type="submit"
                    style={{
                      ...styles.sendButton,
                      width: "50%",
                      opacity: false ? 0.6 : 1,
                      cursor: false ? "not-allowed" : "pointer",
                      backgroundColor: "#E5B611",
                    }}
                  >
                    Search
                  </button>
                </div>
              </Grid>
            </Grid>
            <div style={styles.tableContainer}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", }}>
                <div style={styles.inputLabel}>{"Results"}</div>
                <div>
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                  {this.renderDownloadStatus()}
                  {this.emailCount() > 1 ? (
                    <ButtonWithConfirmation
                      typeConfiguration={{ secondary: true }}
                      onPrimaryAction={this.handleBatchDownload}
                      disabled={
                        (selectedIds.length < 1 && !selectAllActive) ||
                        this.props.isLoadingDownloads ||
                        disableRepeatDownload
                      }
                      openDialogButtonContent={`Download Selected Files (${this.state.selectionTotalCount})`}
                      primaryButtonContent="Download"
                      secondaryButtonContent="Cancel"
                      primaryContent={() => <p>Are you sure you wish to download all files?</p>}
                      secondaryContent={() => (
                        <div className="items-center">
                          <p>
                            Due to the volume of files, this email will be sent as {this.emailCount()}{" "}
                            separate emails.
                          </p>
                        </div>
                      )}
                      icon={DialogIcon.CAUTION}
                      getIconProps={{ large: true }}
                    />
                  ) : (
                    <Button
                      data-qa="submit-announcement"
                      onClick={this.handleBatchDownload}
                      disabled={
                        (selectedIds.length < 1 && !selectAllActive) ||
                        this.props.isLoadingDownloads ||
                        disableRepeatDownload
                      }
                      hasTooltip={disableRepeatDownload}
                      style={{
                        ...styles.sendButton,
                        background: (selectedIds.length < 1 && !selectAllActive) ? "#929497" : "#E5B611",
                        backgroundColor: (selectedIds.length < 1 && !selectAllActive) ? "#929497" : "#E5B611",
                        color: (selectedIds.length < 1 && !selectAllActive) ? "white" : "#000000",
                        opacity: selectedIds.length < 1 && !selectAllActive ? 0.6 : 1,
                        cursor: selectedIds.length < 1 && !selectAllActive ? "not-allowed" : "pointer",
                        marginBottom: "15px",
                        maxWidth: "265px",
                        width: "100%",
                        textTransform: "capitalize",
                      }}
                    >
                      <Tooltip message="Please select a different set of files to download." />
                      {`${
                        selectedIds.length < 1 && !selectAllActive
                          ? "Select file to download"
                          : `Download Selected Files (${this.state.selectionTotalCount})`
                      }`}
                    </Button>
                  )}
                  </div>
                </div>
              </div>
              <LightThemeTableWithPagination
                data={tableData}
                columns={tableColumnsConfig}
                currentPage={(this.state.query.page || 0) + 1}
                pageSize={this.state.query.size || IRIS_DOWNLOADS_PAGE_SIZE}
                totalPages={filesPageCount || 0}
                pageSizeOptions={[10, 25, 50, 100, 200]}
                isLoading={isLoadingFiles}
                onPageChange={handlePageChange}
                onSortChange={handleSortChange}
              />
            </div>
          </Form>
        </Card>
      </div>
    );
  }
}

// ==================== Redux Connection ====================
function formatCreatedDate(utcIsoTimeString: string): string {
  return moment.utc(utcIsoTimeString).utcOffset(-6).format("ddd MMM D YYYY");
}

const connector = connect(
  (state: State) => ({
    files: state.dir_files.content?.data,
    filesPageCount: state.dir_files.content?.pageCount,
    filesPageIndex: state.dir_files.content?.pageIndex,
    batch_download: state.batch_download.content,
    hasError: state.batch_download.error,
    isLoadingDownloads: state.batch_download.loading,
    isLoadingFiles: state.dir_files.loading,
  }),
  {
    createAnalyticsEvent,
    initDIRFiles,
    fetchDIRFiles,
    clearDIRFiles,
    fetchZipForIrisDownload,
  }
);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DirDownloads);

const styles = {
  container: {
    backgroundColor: "#fff",
    padding: 20,
    borderRadius: 16,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  tableContainer: {
    backgroundColor: "#fff",
    padding: 20,
    borderRadius: 16,
    marginTop: "1rem",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  tableHeader: {
    fontSize: 14,
    color: "#333",
    fontWeight: 400,
    marginBottom: 8,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: { fontSize: 24, fontWeight: "normal" as const, color: "#333", margin: 0 },
  noticeBannerWrapper: {
    display: "flex",
    justifyContent: "center",
    margin: "8px 12px 14px",
  },
  noticeBanner: {
    maxWidth: "980px",
    width: "100%",
    backgroundColor: "#dff5df",
    border: "1px solid #9dd89d",
    color: "#1f4d1f",
    borderRadius: 10,
    padding: "12px 16px",
    textAlign: "center" as const,
    fontSize: 14,
    lineHeight: 1.45,
  },
  sendButton: {
    backgroundColor: "#E5B611",
    color: "#000000",
    border: "none",
    padding: "0px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 400,
    height: "30px",
    width: "94px",
  },
  inputRow: { display: "flex", gap: 20, marginBottom: 20 },
  inputGroup: { display: "flex", flexDirection: "column" as const, flex: 1 },
  inputLabel: { fontSize: 14, color: "#333", fontWeight: 400, marginBottom: 8 },
  blankLabel: { fontSize: 14, color: "#333", fontWeight: 400, marginBottom: 8, marginTop: 24 },
  textInput: {
    padding: "12px 15px",
    border: "1px solid #ddd",
    borderRadius: 16,
    fontSize: 14,
    backgroundColor: "#fff",
    height: 44,
    boxSizing: "border-box" as const,
    width: "100%",
  },
  bccRow: { marginBottom: 20 },
  bccHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  bccText: { fontSize: 14, color: "#333", fontWeight: 500 },
  addAuthorText: {
    fontSize: 15,
    color: "#3498db",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 5,
    userSelect: "none" as const,
  },
  errorText: { fontSize: 12, color: "#d32f2f", marginTop: 4 },
  helperText: { fontSize: 12, color: "#666", marginTop: 4 },
} as const;
